import * as THREE from "three";
import { MATS } from "./materials.js";
import { HERO_LOT, HOUSE, LOTS, LOT_STYLE, Z, GRADE } from "./spec.js";

/**
 * The three houses: two storeys of sandy brick under steep charcoal shingle
 * roofs, matching the reference street.  The massing alternates down the row
 * so it reads as near-repetition rather than copy-paste:
 *
 *   west   side-gabled main roof, nested street gable over the garage side
 *   middle full-width street gable (the big brick triangle), darker shingle
 *   east   the west house mirrored in composition -- nested gable over the
 *          porch side, shallower pitch, palest brick
 *
 * The garage stays on +X and the door on -X for all three because LOTS pins
 * garageX/doorX and road.js aims the driveways at them; the "mirroring" is
 * done with the roof, chimney and window composition instead.
 *
 * Cat route up the middle house, every hop 0.80-0.85 m against a 0.95 m jump:
 *   steps -> porch landing 0.51 -> railing cap 1.41 -> chimney shoulder 2.25
 *   -> upper shoulder 3.05 -> porch roof 3.90 -> bedroom window sill 4.55.
 * The bay-window cap (2.22) is reachable from the railing on every house.
 */

const F = HOUSE.foundation;               // 0.55 brick plinth height
const TOP = F + HOUSE.wallHeight;         // 6.75 top of the brickwork
/**
 * Underside of the plinth.
 *
 * A house stands level on its bench (spec.benchY) but the ground under it
 * does not: it ramps from the front elevation down to the full GRADE.drop by
 * the back wall.  So the plinth is carried down past the lowest ground its
 * footprint touches -- the toe of the ramp, plus 0.06 to bury the edge rather
 * than leave it coplanar with the lawn and shimmering -- while its top stays
 * exactly where the brickwork wants it.  Showing more foundation at the
 * downhill end is what a house on a slope does; showing daylight under itself
 * is a bug, and until the ramp arrived the retaining wall in ground.js was
 * what hid it.
 *
 * It buys the contact shading back for free, too: Builder.shade darkens the
 * bottom 62% of a piece, which on a 1.41 m plinth lands almost exactly on the
 * part that is underground at the front and exposed at the back.
 */
const PLINTH = -GRADE.drop - 0.06;
const FRONT = Z.houseFront;               // 1.5  front wall plane
const BACK = Z.houseBack;                 // -11.5
const ZC = (FRONT + BACK) / 2;            // -5
const OV = HOUSE.eaveOverhang;            // 0.5

const PF = 3 * HOUSE.stepRise;            // 0.51 porch floor: three risers
const RAIL = PF + 0.9;                    // 1.41 railing cap top
const SOFFIT = PF + HOUSE.porchHeight;    // 3.51 porch ceiling

/** Per-house composition. uppers = upper-window x offsets from lot centre. */
const VARIANTS = [
  { mainH: 3.6, crossH: 2.4, crossAt: "garage", roof: "shingle",
    uppers: [-3.15, 0.35], stackAt: [2.5, -3.5] },
  { mainH: 3.6, crossAt: null, roof: "shingleAlt",
    uppers: [-3.4, 0, 3.4], ladder: true },
  { mainH: 3.3, crossH: 2.2, crossAt: "porch", roof: "shingle",
    uppers: [-3.1, 1.1], stackAt: [-1.9, -8.0] },
];

export function build(b) {
  for (let i = 0; i < 3; i++) {
    if (i === HERO_LOT) continue;   // hero/ builds this one from a photograph
    house(b, LOTS[i], LOT_STYLE[i], VARIANTS[i], i);
  }
}

// ---------------------------------------------------------------------------
function house(b, lot, style, v, i) {
  const h = lot.house;
  const cx = lot.centreX;
  const brick = MATS[style.brick];
  const door = MATS[style.door];
  const roofMat = MATS[v.roof];
  const gx = h.garageX;

  // -- plinth and shell ------------------------------------------------------
  // The plinth is split so the garage opening reads as a recess right down to
  // the driveway instead of having a brick band across the door's bottom.
  // The main block runs from PLINTH up, which also seals the crawl space: it
  // used to start at 0 with the garden behind it 0.80 m lower, and a gap that
  // size under the back wall is one the cat simply walks in through.
  b.block(cx, PLINTH, (BACK - 0.12 + FRONT - 0.06) / 2, HOUSE.width + 0.24,
          F - PLINTH, FRONT - 0.06 - (BACK - 0.12), brick);
  const bandL = [h.minX - 0.12, gx - 1.92];
  const bandR = [gx + 1.92, h.maxX + 0.12];
  b.block((bandL[0] + bandL[1]) / 2, 0, FRONT + 0.03,
          bandL[1] - bandL[0], F, 0.18, brick);
  b.block((bandR[0] + bandR[1]) / 2, 0, FRONT + 0.03,
          bandR[1] - bandR[0], F, 0.18, brick);
  b.block(cx, F, ZC, HOUSE.width, HOUSE.wallHeight, HOUSE.depth, brick);

  // -- roof ------------------------------------------------------------------
  if (v.crossAt) roofSide(b, lot, roofMat, brick, v);
  else roofFront(b, lot, roofMat, brick, v.mainH);

  // chimneys: an external breast with cat-climbable shoulders on the middle
  // house, a plain stack through the roof plane on the others
  if (v.ladder) chimneyLadder(b, h, brick);
  else {
    const [dx, sz] = v.stackAt;
    const slope = v.mainH / (HOUSE.depth / 2 + OV);
    const surf = TOP + slope * (sz < ZC ? sz - (BACK - OV) : FRONT + OV - sz);
    roofStack(b, cx + dx, sz, surf - 0.9, TOP + v.mainH + 0.45, brick);
  }

  // -- front elevation -------------------------------------------------------
  const px0 = h.minX + 0.15, px1 = px0 + HOUSE.porchWidth;
  porch(b, h.doorX, px0, px1, roofMat, i);
  frontDoor(b, h.doorX, door, i + 2);
  garageDoor(b, gx);
  bay(b, brick, px1 + 0.33, gx - 1.8 - 0.32);

  for (const [k, dx] of v.uppers.entries()) {
    // the middle house's porch-side sill is a deep stone perch the cat can
    // reach from the porch roof (3.90 + 0.95 > 4.55); the rest are dressing
    const perch = v.ladder && k === 0;
    winBox(b, { axis: "z", pos: FRONT, dir: 1 }, cx + dx, 4.55, 1.1, 1.5,
           { sillMat: MATS.stoneTrim, sillD: perch ? 0.26 : 0.2,
             sillOff: perch ? 0.07 : 0.045, perch });
  }

  // cream corner boards on the street corners
  b.block(h.minX + 0.07, F, FRONT + 0.02, 0.14, HOUSE.wallHeight, 0.1,
          MATS.trim, { solid: false });
  b.block(h.maxX - 0.07, F, FRONT + 0.02, 0.14, HOUSE.wallHeight, 0.1,
          MATS.trim, { solid: false });

  // -- sides and back --------------------------------------------------------
  const sideX = i === 0 ? h.minX : h.maxX;          // outer wall of the row,
  const sideDir = i === 0 ? -1 : 1;                 // +x wall for the middle
  winBox(b, { axis: "x", pos: sideX, dir: sideDir }, -4.0, 4.6, 1.0, 1.4, {});

  const bx = cx + [-2.2, 1.8, -1.8][i];
  backEntry(b, bx, door);
  for (const dx of [-2.8, 2.8])
    winBox(b, { axis: "z", pos: BACK, dir: -1 }, cx + dx, 4.55, 1.1, 1.4, {});
  winBox(b, { axis: "z", pos: BACK, dir: -1 }, cx + (bx > cx ? -2.5 : 2.5),
         1.55, 1.3, 1.3, {});

  b.mark("door" + i, h.doorX, F, FRONT + 0.4);
  b.mark("backdoor" + i, bx, F, BACK - 0.4);
  if (v.ladder) b.mark("porchRoofCat", (px0 + px1) / 2, 3.9, 2.6);
}

// ---------------------------------------------------------------------------
// Roof shapes.  Gable-end walls are thin brick prisms made with b.roof and
// pushed proud of the shingle prism, so the street sees brick triangles with
// cream rakes instead of shingle-textured gable ends.

function roofFront(b, lot, mat, brick, H) {
  const cx = lot.centreX, half = HOUSE.width / 2;
  b.roof(cx, TOP, ZC, HOUSE.width + 2 * OV, HOUSE.depth, H, mat,
         { ridge: "z", tile: 1.1 });
  for (const [z, o] of [[FRONT, 0.07], [BACK, -0.07]])
    b.roof(cx, TOP, z + o, HOUSE.width, 0.18, H, brick,
           { ridge: "z", solid: false });
  rakes(b, cx, TOP, FRONT + 0.1, half, H);
  vent(b, cx, TOP + H - 1.3, FRONT + 0.16, 0.42);
  // fascia + gutter along both side eaves, downspouts on the side walls
  // (the left one sits clear of the chimney breast at z -0.15..1.55)
  for (const s of [-1, 1]) {
    const ex = cx + s * (half + OV);
    b.block(ex - s * 0.06, TOP - 0.2, ZC, 0.14, 0.22, HOUSE.depth, MATS.trim,
            { solid: false });
    b.block(ex + s * 0.08, TOP - 0.13, ZC, 0.12, 0.13, HOUSE.depth - 0.4,
            MATS.metalPale, { solid: false });
    const dz = s < 0 ? -0.6 : 0.9;
    b.block(cx + s * (half + 0.05), 0.05, dz, 0.07, TOP - 0.25, 0.07,
            MATS.metalPale, { solid: false });
    b.block(cx + s * (half + 0.31), TOP - 0.2, dz, 0.56, 0.07, 0.07,
            MATS.metalPale, { solid: false });
  }
}

function roofSide(b, lot, mat, brick, v) {
  const cx = lot.centreX, H = v.mainH;
  const span = HOUSE.depth + 2 * OV;
  b.roof(cx, TOP, ZC, HOUSE.width, span, H, mat, { ridge: "x", tile: 1.1 });
  for (const s of [-1, 1])
    b.roof(cx + s * (HOUSE.width / 2 + 0.09), TOP, ZC, 0.18, HOUSE.depth, H,
           brick, { ridge: "x", solid: false });

  // nested street gable: its ridge dives into the front slope, its brick face
  // sits just proud of the facade below the main eave line
  const gx = v.crossAt === "garage" ? lot.house.garageX
                                    : lot.house.doorX + 0.55;
  const slope = H / (span / 2);
  const zBack = FRONT + OV - v.crossH / slope - 0.13; // buried 0.13 in the slope
  b.roof(gx, TOP, (zBack + FRONT + 0.12) / 2, 5.2, FRONT + 0.12 - zBack,
         v.crossH, mat, { ridge: "z", solid: false, tile: 1.1 });
  b.roof(gx, TOP, FRONT + 0.07, 4.4, 0.18, v.crossH, brick,
         { ridge: "z", solid: false });
  rakes(b, gx, TOP, FRONT + 0.1, 2.2, v.crossH);
  vent(b, gx, TOP + v.crossH - 1.05, FRONT + 0.16, 0.35);

  // front fascia/gutter stops where the nested gable comes through
  const segs = [[lot.house.minX, gx - 2.72], [gx + 2.72, lot.house.maxX]];
  for (const [a0, a1] of segs) {
    if (a1 - a0 < 0.5) continue;
    b.block((a0 + a1) / 2, TOP - 0.2, FRONT + OV - 0.06, a1 - a0, 0.22, 0.14,
            MATS.trim, { solid: false });
    b.block((a0 + a1) / 2, TOP - 0.13, FRONT + OV + 0.08, a1 - a0 - 0.2, 0.13,
            0.12, MATS.metalPale, { solid: false });
  }
  b.block(cx, TOP - 0.2, BACK - OV + 0.06, HOUSE.width, 0.22, 0.14, MATS.trim,
          { solid: false });
  // downspouts: garage-side one rides down over the corner board, the other
  // drops down the outer side wall where nothing else lives
  b.block(lot.house.maxX - 0.07, 0.05, FRONT + 0.11, 0.07, TOP - 0.25, 0.07,
          MATS.metalPale, { solid: false });
  b.block(lot.house.maxX - 0.07, TOP - 0.2, FRONT + 0.33, 0.07, 0.07, 0.52,
          MATS.metalPale, { solid: false });
  b.block(lot.house.minX - 0.05, 0.05, 1.0, 0.07, TOP - 0.25, 0.07,
          MATS.metalPale, { solid: false });
}

/** Cream bargeboards along a street-facing gable's sloped edges. */
function rakes(b, cx, yEave, zFace, half, rise) {
  const ang = Math.atan2(rise, half);
  const len = Math.hypot(half, rise) + 0.25;
  for (const s of [-1, 1]) {
    const g = new THREE.BoxGeometry(len, 0.16, 0.12);
    g.rotateZ(s * ang);
    g.translate(cx - s * half / 2, yEave + rise / 2 + 0.04, zFace);
    b.add(g, MATS.trim, { tile: 0 });
  }
}

/** Half-round louvre vent high in a gable, flat side down. */
function vent(b, x, y, zFace, r) {
  const g = new THREE.CylinderGeometry(r, r, 0.1, 12, 1, false, 0, Math.PI);
  g.rotateX(Math.PI / 2);
  g.rotateZ(Math.PI / 2);
  g.translate(x, y, zFace + 0.05);
  b.add(g, MATS.trim, { tile: 0 });
  // plate sealing the open chord so there is no see-through slit from below
  b.block(x, y - 0.03, zFace + 0.05, 2 * r, 0.04, 0.1, MATS.trimShade,
          { solid: false });
}

function roofStack(b, x, z, yBase, yTop, brick) {
  b.block(x, yBase, z, 0.74, yTop - yBase, 0.74, brick);
  b.block(x, yTop, z, 0.9, 0.1, 0.9, MATS.stoneTrim, { solid: false });
  b.block(x, yTop + 0.1, z, 0.18, 0.18, 0.18, MATS.metalDark, { solid: false });
}

/**
 * The middle house's external chimney breast: two weathering shoulders climb
 * 1.41 -> 2.25 -> 3.05, which is what lets the cat top out on the porch roof.
 * Ledges are 0.25-0.3 m deep -- wider than the cat's 0.24 m collider.
 */
function chimneyLadder(b, h, brick) {
  const wall = h.minX;
  // The breast wraps 0.12 past the front corner so each shoulder's front
  // ledge sits proud of the wall plane -- the cat's jump from the porch
  // railing then never has to shave the house corner collider.
  b.block(wall - 0.26 + 0.025, 0, 0.71, 0.57, 2.25, 1.82, brick);
  b.block(wall - 0.2 + 0.025, 2.25, 0.71, 0.45, 0.8, 1.2, brick);
  b.block(wall - 0.15 + 0.025, 3.05, 0.71, 0.35, 4.75, 0.6, brick);
  b.block(wall - 0.17, 7.8, 0.71, 0.4, 0.12, 0.76, MATS.stoneTrim,
          { solid: false });
  b.block(wall - 0.17, 7.92, 0.71, 0.16, 0.16, 0.16, MATS.metalDark,
          { solid: false });
}

// ---------------------------------------------------------------------------
/**
 * A window: cream surround proud of the brick, glass recessed behind it,
 * slim mullions splitting the panes.  wall = {axis, pos, dir} picks the
 * elevation; u is the centre along that wall.
 */
function winBox(b, wall, u, yb, w, h, o) {
  const place = (cu, y, wu, hy, th, off, mat, solid = false) => {
    if (wall.axis === "z")
      b.block(cu, y, wall.pos + wall.dir * off, wu, hy, th, mat, { solid });
    else b.block(wall.pos + wall.dir * off, y, cu, th, hy, wu, mat, { solid });
  };
  place(u - w / 2 - 0.05, yb, 0.1, h, 0.14, 0.02, MATS.trim);
  place(u + w / 2 + 0.05, yb, 0.1, h, 0.14, 0.02, MATS.trim);
  place(u, yb + h, w + 0.2, 0.1, 0.14, 0.02, MATS.trim);
  place(u, yb - 0.08, w + 0.22, 0.08, o.sillD ?? 0.18, o.sillOff ?? 0.04,
        o.sillMat ?? MATS.trim, !!o.perch);
  place(u, yb, w, h, 0.05, 0.005, MATS.glass);
  place(u, yb, 0.05, h, 0.03, 0.04, MATS.trim);            // vertical mullion
  place(u, yb + h / 2 - 0.025, w, 0.05, 0.03, 0.04, MATS.trim);
}

/**
 * The wide box window between porch and garage.  The cap is solid and its
 * top sits at 2.22 m: reachable from the porch railing (1.41 + 0.95), and the
 * hidden collider over the glass keeps the cat from clipping inside the bay.
 */
function bay(b, brick, x0, x1) {
  const bw = x1 - x0, bx = (x0 + x1) / 2;
  b.block(bx, 0, FRONT + 0.28, bw, 1.0, 0.56, brick);
  b.collider(bx, 1.0, FRONT + 0.26, bw - 0.2, 1.1, 0.5);
  for (const s of [-1, 1])
    b.block(bx + s * (bw / 2 - 0.05), 1.0, FRONT + 0.52, 0.1, 1.1, 0.1,
            MATS.trim, { solid: false });
  b.block(bx, 0.94, FRONT + 0.31, bw + 0.16, 0.08, 0.62, MATS.trim,
          { solid: false });
  b.block(bx, 1.02, FRONT + 0.5, bw - 0.3, 1.0, 0.05, MATS.glass,
          { solid: false });
  for (const s of [-1, 1])
    b.block(bx + s * (bw - 0.3) / 6, 1.02, FRONT + 0.53, 0.05, 1.0, 0.03,
            MATS.trim, { solid: false });
  b.block(bx, 2.02, FRONT + 0.3, bw + 0.1, 0.08, 0.6, MATS.trim,
          { solid: false });
  b.block(bx, 2.1, FRONT + 0.32, bw + 0.24, 0.12, 0.72, MATS.trim);
}

function garageDoor(b, gx) {
  b.block(gx, 0.04, FRONT + 0.03, HOUSE.garageWidth, HOUSE.garageHeight - 0.02,
          0.06, MATS.garageDoor, { solid: false });
  // sectional panel lines, drawn as slim shaded bars sitting on the door skin
  for (const y of [0.68, 1.32, 1.96])
    b.block(gx, y, FRONT + 0.06, HOUSE.garageWidth - 0.16, 0.055, 0.04,
            MATS.trimShade, { solid: false });
  for (const s of [-1, 1])
    b.block(gx + s * (HOUSE.garageWidth / 2 + 0.06), 0.04, FRONT + 0.05, 0.14,
            HOUSE.garageHeight + 0.1, 0.14, MATS.trim, { solid: false });
  b.block(gx, HOUSE.garageHeight + 0.02, FRONT + 0.05, HOUSE.garageWidth + 0.4,
          0.16, 0.14, MATS.trim, { solid: false });
}

function frontDoor(b, doorX, doorMat, digits) {
  b.block(doorX, F, FRONT + 0.035, HOUSE.doorWidth, HOUSE.doorHeight, 0.07,
          doorMat, { solid: false });
  for (const s of [-1, 1])
    b.block(doorX + s * (HOUSE.doorWidth / 2 + 0.05), F - 0.04, FRONT + 0.05,
            0.1, HOUSE.doorHeight + 0.12, 0.12, MATS.trim, { solid: false });
  b.block(doorX, F + HOUSE.doorHeight, FRONT + 0.05, HOUSE.doorWidth + 0.3,
          0.12, 0.12, MATS.trim, { solid: false });
  b.block(doorX + 0.44, 1.55, FRONT + 0.085, 0.05, 0.16, 0.05, MATS.metalDark,
          { solid: false });
  // lantern one side of the door, house-number plaque the other
  const lx = doorX + 0.83;
  b.block(lx, 2.08, FRONT + 0.02, 0.04, 0.1, 0.06, MATS.metalDark,
          { solid: false });
  b.block(lx, 1.86, FRONT + 0.06, 0.11, 0.18, 0.11, MATS.glass,
          { solid: false });
  b.block(lx, 2.04, FRONT + 0.06, 0.15, 0.05, 0.15, MATS.metalDark,
          { solid: false });
  const nx = doorX - 0.86;
  b.block(nx, 1.72, FRONT + 0.025, 0.3, 0.36, 0.05, MATS.trim,
          { solid: false });
  for (let d = 0; d < digits; d++)
    b.block(nx + (d - (digits - 1) / 2) * 0.08, 1.82, FRONT + 0.055, 0.045,
            0.16, 0.03, MATS.metalDark, { solid: false });
}

// ---------------------------------------------------------------------------
function porch(b, doorX, px0, px1, roofMat, i) {
  const pcx = (px0 + px1) / 2;
  const pcz = FRONT + HOUSE.porchDepth / 2;                 // 2.6
  const zEdge = FRONT + HOUSE.porchDepth;                   // 3.7

  b.block(pcx, 0, pcz, HOUSE.porchWidth, PF, HOUSE.porchDepth, MATS.concrete);
  // two steps below the landing, centred on the door; 0.17 risers per spec
  b.block(doorX, 0, zEdge + 0.15, 1.4, 2 * HOUSE.stepRise, 0.3, MATS.concrete);
  b.block(doorX, 0, zEdge + 0.45, 1.4, HOUSE.stepRise, 0.3, MATS.concrete);

  for (const x of [px0 + 0.2, px1 - 0.2]) {
    b.block(x, PF, zEdge - 0.2, 0.18, HOUSE.porchHeight, 0.18, MATS.trim);
    b.block(x, PF, zEdge - 0.2, 0.26, 0.12, 0.26, MATS.trim, { solid: false });
    b.block(x, SOFFIT - 0.1, zEdge - 0.2, 0.26, 0.1, 0.26, MATS.trim,
            { solid: false });
  }

  b.block(pcx, SOFFIT, pcz, HOUSE.porchWidth + 0.5, 0.15, HOUSE.porchDepth
          + 0.7, MATS.trim, { solid: false });
  b.roof(pcx, SOFFIT + 0.15, pcz, HOUSE.porchWidth + 0.5,
         HOUSE.porchDepth + 0.7, 0.75, roofMat,
         { ridge: "x", solid: false, tile: 0.8 });
  // flattened stand-on collider (top 3.90): reachable from the middle house's
  // chimney shoulder at 3.05, and a fair perch height for the sloped shape
  b.collider(pcx, 3.55, pcz, HOUSE.porchWidth + 0.5, 0.35,
             HOUSE.porchDepth + 0.7);

  rail(b, "x", zEdge - 0.2, px0 + 0.29, doorX - 0.85);
  rail(b, "x", zEdge - 0.2, doorX + 0.85, px1 - 0.29);
  rail(b, "z", px0 + 0.2, FRONT + 0.12, zEdge - 0.29);
  rail(b, "z", px1 - 0.2, FRONT + 0.12, zEdge - 0.29);
  for (const s of [-1, 1]) {                                // newels at steps
    b.block(doorX + s * 0.85, PF, zEdge - 0.2, 0.1, 0.9, 0.1, MATS.trim);
    b.block(doorX + s * 0.85, RAIL, zEdge - 0.2, 0.16, 0.05, 0.16, MATS.trim,
            { solid: false });
  }
  b.mark("porch" + i, pcx, PF, pcz);
}

/** Railing run: solid cap (a cat balance beam at 1.41) over thin balusters. */
function rail(b, ax, fixed, a0, a1) {
  const len = a1 - a0;
  if (len < 0.25) return;
  const c = (a0 + a1) / 2;
  if (ax === "x") b.block(c, RAIL - 0.06, fixed, len, 0.06, 0.12, MATS.trim);
  else b.block(fixed, RAIL - 0.06, c, 0.12, 0.06, len, MATS.trim);
  const n = Math.max(1, Math.round(len / 0.42) - 1);
  for (let k = 1; k <= n; k++) {
    const p = a0 + (len * k) / (n + 1);
    if (ax === "x")
      b.block(p, PF, fixed, 0.04, RAIL - 0.06 - PF, 0.04, MATS.trim,
              { solid: false });
    else b.block(fixed, PF, p, 0.04, RAIL - 0.06 - PF, 0.04, MATS.trim,
                 { solid: false });
  }
}

function backEntry(b, bx, doorMat) {
  b.block(bx, F, BACK - 0.035, 1.0, 2.1, 0.07, doorMat, { solid: false });
  for (const s of [-1, 1])
    b.block(bx + s * 0.55, F - 0.04, BACK - 0.05, 0.1, 2.22, 0.12, MATS.trim,
            { solid: false });
  b.block(bx, F + 2.1, BACK - 0.05, 1.3, 0.12, 0.12, MATS.trim,
          { solid: false });
  // The garden is a terrace below the street (spec.GRADE), so the back door
  // is PF above the floor level AND that drop above its own ground: six
  // risers rather than the front's three, each still inside a cat's step.
  const rise = (PF + GRADE.drop) / 6;
  b.block(bx, 0, BACK - 0.48, 1.5, PF + GRADE.drop, 0.95, MATS.concrete);
  for (let k = 1; k <= 5; k++)
    b.block(bx, 0, BACK - 0.95 - (k - 0.5) * 0.28, 1.3,
            (PF + GRADE.drop) - k * rise, 0.28, MATS.concrete);
}
